/**
 * Sentry FE PII 마스킹 Scrubber — issue #351 (admin#1304 / admin#1263 정합 이식).
 *
 * Sentry 가 국외 (US/EU) 위탁자이므로 LFPDPPP 정합성 위해 PII 자동 redact 강제.
 * `sendDefaultPii=false` 만으론 IP/cookies 만 차단 → request body / extra context /
 * breadcrumbs / message / contexts / exception 의 자유 텍스트 PII 는 별도 패턴 + 키 마스킹 필요.
 *
 * ## 마스킹 영역 (8 영역)
 * 1. request (body / data / query_string / headers)
 * 2. extra (재귀 객체)
 * 3. tags (값만, 키 보존)
 * 4. user (email + username redact, **id 보존**, ip null 강제)
 * 5. breadcrumbs (message + data)
 * 6. message (Event 본체)
 * 7. contexts (사용자 정의 — runtime/os/device/browser/app 보존)
 * 8. exception (values[].value)
 *
 * ## 마스킹 패턴 (5 패턴, 우선순위 순)
 * - JWT (가장 구체적)
 * - Bearer 토큰
 * - 신용카드 (13~19 자리, 전화 패턴 앞)
 * - email
 * - 멕시코 전화 (10자리 정확, 9자리 거래 ID 회피)
 *
 * ## 민감 키 (값 통째 redact)
 * password / pwd / secret / token / authorization / cookie / session /
 * api_key / access_token / refresh_token /
 * rfc / curp / nss / clabe / ine / voter_id / elector_key /
 * ssn / cpf / tax_id / credit_card / card_number / cvv / cvc / pan.
 *
 * ## Karpathy 가정 surface
 * - regex 실패 → fail-safe '[REDACTED]' (silent passthrough 금지)
 * - Sentry SDK 내부 구조 의존성 0 (불변 객체 재구성)
 * - 키 매칭은 substring + lowercase (대소문자 회피)
 *
 * @see /opt/MxOlulo/olulo-mx-admin/app/Support/Sentry/PiiScrubber.php
 */

import type {
  Breadcrumb,
  ErrorEvent,
  Event,
  EventHint,
} from '@sentry/react';

export type ScrubbableTransactionEvent = Event & { type: 'transaction' };

const REDACTED = '[REDACTED]';
const TERMINAL_TELEMETRY_EVENTS = new Set([
  'table_session_terminal',
  'session_ended',
]);

/**
 * 패턴 우선순위 — 더 구체적인 패턴부터.
 * 긴 숫자열을 짧은 패턴이 부분 매칭하는 회귀 차단.
 */
const PATTERNS: ReadonlyArray<{ pattern: RegExp; replacement: string }> = [
  // JWT (가장 구체적)
  { pattern: /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, replacement: REDACTED },
  // Bearer 토큰
  { pattern: /[Bb]earer\s+[A-Za-z0-9._-]+/g, replacement: 'Bearer [REDACTED]' },
  // 신용카드 (13~19 자리) — 전화 패턴 앞에 배치
  { pattern: /\b\d{13,19}\b/g, replacement: REDACTED },
  // email — RFC 5322 단순화
  { pattern: /[\w.+-]+@[\w-]+\.[\w.-]+/g, replacement: REDACTED },
  // 멕시코 전화: 정확히 10자리 (2-4-4 형식, +52 / 1 prefix 선택). 9자리 raw 숫자 (거래 ID 등) 회피.
  {
    pattern: /(?:\+?52[\s-]?)?(?:1[\s-]?)?\d{2}[\s-]?\d{4}[\s-]?\d{4}\b/g,
    replacement: REDACTED,
  },
];

/**
 * 민감 키 목록 (substring 매칭, lowercase).
 *
 * admin PiiScrubber.php SENSITIVE_KEYS + FE 관습 키 (cookie / session / pan) 추가.
 */
const SENSITIVE_KEYS: ReadonlyArray<string> = [
  // 일반
  'password',
  'pwd',
  'secret',
  'token',
  'authorization',
  'cookie',
  'session',
  'api_key',
  'apikey',
  'access_token',
  'refresh_token',
  // 멕시코 LFPDPPP 식별자
  'rfc',
  'curp',
  'nss',
  'clabe',
  'ine',
  'voter_id',
  'elector_key',
  // 국제 식별자 / 카드
  'ssn',
  'cpf',
  'tax_id',
  'credit_card',
  'card_number',
  'cvv',
  'cvc',
  'pan',
];

/**
 * SDK 표준 contexts — PII 없음 + 디버깅 가치 → 마스킹 skip.
 */
const STANDARD_CONTEXT_KEYS: ReadonlyArray<string> = [
  'runtime',
  'os',
  'device',
  'browser',
  'app',
  'culture',
  'trace',
];

/**
 * 패턴 마스킹. regex 실패 시 fail-safe REDACTED.
 * silent passthrough (원본 PII 누출) 금지.
 */
function scrubString(value: string): string {
  let result = value;
  for (const rule of PATTERNS) {
    try {
      result = result.replace(rule.pattern, rule.replacement);
    } catch {
      // regex / UTF-8 실패 → 원본 보존 금지
      return REDACTED;
    }
  }
  return scrubOpaquePathSegments(result);
}

/**
 * Credentials appear in API/QR paths and waitlist deep-link query values.
 * Restrict free-text replacement to these route families so unrelated
 * identifiers remain useful for diagnostics.
 */
function scrubOpaquePathSegments(value: string): string {
  return value
    .replace(/\/api\/v1\/sessions\/[^/?#\s]+/g, `/api/v1/sessions/${REDACTED}`)
    .replace(/\/api\/v1\/waitlist\/[^/?#\s]+/g, `/api/v1/waitlist/${REDACTED}`)
    .replace(/\/qr\/[^/?#\s]+/g, `/qr/${REDACTED}`)
    .replace(/\/fila\/[^\s"'<>]+/g, (url) =>
      url.replace(/([?&](?:t|%74)=)[^&#\s"'<>]*/gi, `$1${REDACTED}`),
    );
}

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return normalized === 't' || SENSITIVE_KEYS.some((needle) => normalized.includes(needle));
}

function decodeQueryComponent(value: string): string | null {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return null;
  }
}

function encodeScrubbedQueryComponent(value: string): string {
  return encodeURIComponent(value).replace(/%5BREDACTED%5D/g, REDACTED);
}

function scrubPossiblyEncodedComponent(value: string): string {
  const rawScrubbed = scrubString(value);
  const decoded = decodeQueryComponent(value);
  if (decoded === null || decoded === value) {
    return rawScrubbed;
  }

  const scrubbedDecoded = scrubString(decoded);
  if (scrubbedDecoded === decoded) {
    return rawScrubbed;
  }

  return encodeScrubbedQueryComponent(scrubbedDecoded);
}

function scrubQueryLikePart(part: string): string {
  const eq = part.indexOf('=');
  if (eq === -1) {
    const decodedPart = decodeQueryComponent(part) ?? part;
    return isSensitiveKey(decodedPart) ? REDACTED : scrubPossiblyEncodedComponent(part);
  }

  const key = part.slice(0, eq);
  const value = part.slice(eq + 1);
  const decodedKey = decodeQueryComponent(key) ?? key;

  if (isSensitiveKey(decodedKey)) {
    return `${key}=${REDACTED}`;
  }

  return `${key}=${scrubPossiblyEncodedComponent(value)}`;
}

/**
 * URL/query/fragment 형태 문자열은 percent-encoded 값이 regex 를 우회할 수 있으므로
 * component decode 후 값 단위로 다시 마스킹한다. 무해한 값은 원래 인코딩을 보존한다.
 */
function scrubQueryLikeString(value: string): string {
  const prefix = value.startsWith('?') || value.startsWith('#') ? value[0] : '';
  const body = prefix ? value.slice(1) : value;

  if (!body.includes('=') && !body.includes('&')) {
    return `${prefix}${scrubPossiblyEncodedComponent(body)}`;
  }

  return `${prefix}${body.split('&').map((part) => scrubQueryLikePart(part)).join('&')}`;
}

function scrubUrlString(value: string): string {
  const hashIndex = value.indexOf('#');
  const beforeHash = hashIndex === -1 ? value : value.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? null : value.slice(hashIndex + 1);
  const queryIndex = beforeHash.indexOf('?');
  const base = queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex);
  const query = queryIndex === -1 ? null : beforeHash.slice(queryIndex + 1);

  return [
    scrubString(base),
    query === null ? '' : `?${scrubQueryLikeString(query)}`,
    fragment === null ? '' : `#${scrubQueryLikeString(fragment)}`,
  ].join('');
}

/**
 * 재귀적으로 임의 값 (string / array / object / primitive) 마스킹.
 *
 * - string → 패턴 마스킹
 * - array → 각 항목 재귀
 * - plain object → 키별 (민감 키 → REDACTED, else 재귀)
 * - 기타 (number / boolean / null / undefined) → 그대로
 */
function scrubValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return scrubString(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item));
  }
  if (value !== null && typeof value === 'object') {
    return scrubObject(value as Record<string, unknown>);
  }
  return value;
}

function scrubObject(data: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(data)) {
    if (isSensitiveKey(key)) {
      result[key] = REDACTED;
      continue;
    }
    result[key] = scrubValue(val);
  }
  return result;
}

function scrubEventExtra(event: Event): void {
  if (!event.extra || typeof event.extra !== 'object') return;

  const preserveNumericSessionId =
    typeof event.message === 'string' && TERMINAL_TELEMETRY_EVENTS.has(event.message);
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(event.extra)) {
    if (
      preserveNumericSessionId &&
      key === 'session_id' &&
      typeof value === 'number' &&
      Number.isFinite(value)
    ) {
      result[key] = value;
      continue;
    }

    result[key] = isSensitiveKey(key) ? REDACTED : scrubValue(value);
  }

  event.extra = result;
}

function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const next: Breadcrumb = { ...breadcrumb };
  if (typeof next.message === 'string') {
    next.message = scrubString(next.message);
  }
  if (next.data && typeof next.data === 'object') {
    next.data = scrubObject(next.data as Record<string, unknown>);
  }
  return next;
}

/**
 * Sentry beforeSend 진입점.
 *
 * @sentry/react 의 `Event` 타입 (BaseTransportOptions 의 SendEventOptions)
 * 위에서 inplace 변형 (Sentry SDK 가 동일 객체 반환 패턴 가정).
 */
export function scrubEvent<E extends Event>(event: E): E {
  // 1. request
  if (event.request) {
    const req = event.request;
    // url 자유 텍스트 (쿼리 파라미터 / fragment 에 PII 누출 가능)
    if (typeof req.url === 'string') {
      req.url = scrubUrlString(req.url);
    }
    if (typeof req.data === 'string') {
      req.data = scrubString(req.data);
    } else if (req.data && typeof req.data === 'object') {
      req.data = scrubObject(req.data as Record<string, unknown>);
    }
    // Sentry SDK 타입: query_string?: string | Record<string, string>
    // R3 시정 — object 형태 분기 누락 회귀 차단.
    if (typeof req.query_string === 'string') {
      req.query_string = scrubQueryLikeString(req.query_string);
    } else if (req.query_string && typeof req.query_string === 'object') {
      const scrubbed: Record<string, string> = {};
      for (const [key, value] of Object.entries(
        req.query_string as Record<string, string>,
      )) {
        scrubbed[key] = isSensitiveKey(key)
          ? REDACTED
          : typeof value === 'string'
            ? scrubString(value)
            : value;
      }
      req.query_string = scrubbed;
    }
    if (req.headers && typeof req.headers === 'object') {
      req.headers = scrubObject(req.headers as Record<string, unknown>) as Record<string, string>;
    }
    // 타입은 Record<string, string> 이지만 런타임상 raw cookie string 이 들어올 수 있음
    // (예: 수동으로 setContext('request', { cookies: document.cookie }) 사용 시).
    const rawCookies = req.cookies as unknown;
    if (typeof rawCookies === 'string') {
      // cookie string (예: "sessionid=abc; auth_token=xyz") 은 key=value pair 단위로 파싱하여
      // sensitive key 의 value 를 [REDACTED] 처리. 단순 regex 만으로는 random secret 이 누출됨.
      req.cookies = rawCookies
        .split(/;\s*/)
        .map((part) => {
          const eq = part.indexOf('=');
          if (eq === -1) {
            return scrubString(part);
          }
          const key = part.slice(0, eq).trim();
          const value = part.slice(eq + 1);
          return `${key}=${isSensitiveKey(key) ? REDACTED : scrubString(value)}`;
        })
        .join('; ') as unknown as Record<string, string>;
    } else if (rawCookies && typeof rawCookies === 'object') {
      req.cookies = scrubObject(rawCookies as Record<string, unknown>) as Record<string, string>;
    }
    // env (Record<string, string>) — 사용자 정의 키에 PII 누출 가능
    const reqWithEnv = req as { env?: unknown };
    if (reqWithEnv.env && typeof reqWithEnv.env === 'object') {
      reqWithEnv.env = scrubObject(reqWithEnv.env as Record<string, unknown>);
    }
    // fragment (URL # 뒤 자유 텍스트)
    const reqWithFragment = req as { fragment?: unknown };
    if (typeof reqWithFragment.fragment === 'string') {
      reqWithFragment.fragment = scrubQueryLikeString(reqWithFragment.fragment);
    }
  }

  // 2. extra
  scrubEventExtra(event);

  // 3. tags (값만)
  if (event.tags && typeof event.tags === 'object') {
    const tags: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(event.tags)) {
      tags[key] = typeof val === 'string' ? scrubString(val) : val;
    }
    event.tags = tags as Event['tags'];
  }

  // 4. user
  if (event.user) {
    if (typeof event.user.email === 'string') {
      event.user.email = REDACTED;
    }
    // setUser({ name: '...' }) 경로 PII 누출 차단 (Sentry User 표준 필드)
    const userWithName = event.user as { name?: unknown };
    if (typeof userWithName.name === 'string') {
      userWithName.name = REDACTED;
    }
    if (typeof event.user.username === 'string') {
      event.user.username = REDACTED;
    }
    // ip_address 제거 (디버깅용 id 는 보존)
    if ('ip_address' in event.user) {
      event.user.ip_address = undefined;
    }
  }

  // 5. breadcrumbs
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((bc) => scrubBreadcrumb(bc));
  }

  // 6. message
  if (typeof event.message === 'string') {
    event.message = scrubString(event.message);
  } else if (event.message && typeof event.message === 'object') {
    // structured message { message, params }
    const msg = event.message as { message?: string; params?: unknown[] };
    if (typeof msg.message === 'string') {
      msg.message = scrubString(msg.message);
    }
    if (Array.isArray(msg.params)) {
      msg.params = msg.params.map((p) => scrubValue(p));
    }
  }

  // 7. contexts (SDK 표준 contexts 보존)
  if (event.contexts && typeof event.contexts === 'object') {
    const contexts = event.contexts as Record<string, unknown>;
    for (const [name, ctx] of Object.entries(contexts)) {
      if (STANDARD_CONTEXT_KEYS.includes(name)) {
        continue;
      }
      if (ctx && typeof ctx === 'object') {
        contexts[name] = scrubObject(ctx as Record<string, unknown>);
      }
    }
  }

  // 8. exception values
  if (event.exception?.values && Array.isArray(event.exception.values)) {
    for (const ex of event.exception.values) {
      if (typeof ex.value === 'string') {
        ex.value = scrubString(ex.value);
      }
    }
  }

  return event;
}

export function scrubTransactionEvent<E extends ScrubbableTransactionEvent>(event: E): E {
  scrubEvent(event);

  if (typeof event.transaction === 'string') {
    event.transaction = scrubString(event.transaction);
  }

  if (Array.isArray(event.spans)) {
    for (const span of event.spans) {
      if (typeof span.description === 'string') {
        span.description = scrubString(span.description);
      }
      if (span.data && typeof span.data === 'object') {
        span.data = scrubObject(span.data as Record<string, unknown>) as typeof span.data;
      }
    }
  }

  return event;
}

/**
 * Sentry beforeSend 시그니처 호환 wrapper.
 *
 * `Sentry.init({ beforeSend: createPiiScrubber() })` 형태 사용.
 * `null` 반환 시 이벤트 drop (본 scrubber 는 항상 event 반환).
 */
export function createPiiScrubber(): (
  event: ErrorEvent,
  hint: EventHint,
) => ErrorEvent | null {
  return (event: ErrorEvent): ErrorEvent | null => scrubEvent(event) as ErrorEvent;
}
